import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { tokens } from '../../theme';

/**
 * Shared page furniture used across the portal so every route presents the
 * same institutional structure: a banner, sectioned content, and consistent
 * loading / empty states.
 */

const useHeroStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        backgroundColor: tokens.blue900,
        backgroundImage: `linear-gradient(115deg, ${tokens.blue900} 0%, ${tokens.blue700} 55%, ${tokens.blue600} 100%)`,
        color: '#ffffff',
        overflow: 'hidden',
        borderBottom: `4px solid ${tokens.gold}`,
    },
    pattern: {
        position: 'absolute',
        inset: 0,
        opacity: 0.16,
        backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.65) 1px, transparent 0)',
        backgroundSize: '22px 22px',
        pointerEvents: 'none',
    },
    inner: {
        position: 'relative',
        paddingTop: theme.spacing(7),
        paddingBottom: theme.spacing(7),
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(5),
            paddingBottom: theme.spacing(5),
        },
    },
    innerCompact: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
    eyebrow: {
        display: 'inline-block',
        color: tokens.goldSoft,
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        marginBottom: theme.spacing(1.5),
    },
    title: {
        color: '#ffffff',
        maxWidth: 820,
        marginBottom: theme.spacing(2),
    },
    description: {
        color: 'rgba(255,255,255,0.86)',
        maxWidth: 740,
        fontSize: '1.0625rem',
        lineHeight: 1.7,
        margin: 0,
    },
    crumbs: {
        marginBottom: theme.spacing(2.5),
        '& a': {
            color: 'rgba(255,255,255,0.78)',
            textDecoration: 'none',
            fontSize: '0.8125rem',
            '&:hover': { color: '#ffffff', textDecoration: 'underline' },
        },
    },
    crumbCurrent: {
        color: '#ffffff',
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    crumbSeparator: {
        color: 'rgba(255,255,255,0.45)',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(4),
    },
}));

/**
 * Banner at the top of every inner page.
 * `breadcrumbs` takes `[{ label, to }]`; the last entry renders as plain text.
 */
export function PageHero({
    eyebrow,
    title,
    description,
    breadcrumbs,
    actions,
    compact = false,
    children,
}) {
    const classes = useHeroStyles();

    return (
        <header className={classes.root}>
            <div className={classes.pattern} aria-hidden="true" />
            <Container maxWidth="lg">
                <div className={`${classes.inner} ${compact ? classes.innerCompact : ''}`}>
                    {breadcrumbs && breadcrumbs.length > 0 && (
                        <Breadcrumbs
                            aria-label="Breadcrumb"
                            separator={<NavigateNextIcon fontSize="small" className={classes.crumbSeparator} />}
                            className={classes.crumbs}
                        >
                            <RouterLink to="/">Home</RouterLink>
                            {breadcrumbs.map((crumb, index) =>
                                index === breadcrumbs.length - 1 || !crumb.to ? (
                                    <span key={crumb.label} className={classes.crumbCurrent}>
                                        {crumb.label}
                                    </span>
                                ) : (
                                    <RouterLink key={crumb.label} to={crumb.to}>
                                        {crumb.label}
                                    </RouterLink>
                                )
                            )}
                        </Breadcrumbs>
                    )}

                    {eyebrow && <span className={classes.eyebrow}>{eyebrow}</span>}

                    {title && (
                        <Typography variant="h1" component="h1" className={classes.title}
                            style={{ fontSize: compact ? '2.125rem' : undefined }}>
                            {title}
                        </Typography>
                    )}

                    {description && (
                        <p className={classes.description}>{description}</p>
                    )}

                    {actions && <div className={classes.actions}>{actions}</div>}

                    {children}
                </div>
            </Container>
        </header>
    );
}

const useSectionStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(5),
            paddingBottom: theme.spacing(5),
        },
    },
    tight: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
    default: { backgroundColor: tokens.white },
    muted: { backgroundColor: tokens.surface },
    tint: { backgroundColor: tokens.blue50 },
    brand: {
        backgroundColor: tokens.blue900,
        backgroundImage: `linear-gradient(120deg, ${tokens.blue900}, ${tokens.blue700})`,
        color: '#ffffff',
    },
    bordered: {
        borderTop: `1px solid ${tokens.border}`,
    },
}));

/** Full-bleed page section with a constrained content container. */
export function Section({
    tone = 'default',
    tight = false,
    bordered = false,
    maxWidth = 'lg',
    id,
    component = 'section',
    children,
}) {
    const classes = useSectionStyles();
    const Tag = component;

    return (
        <Tag
            id={id}
            className={[
                classes.root,
                tight ? classes.tight : '',
                classes[tone] || classes.default,
                bordered ? classes.bordered : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <Container maxWidth={maxWidth}>{children}</Container>
        </Tag>
    );
}

const useHeadingStyles = makeStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(4),
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(3),
        flexWrap: 'wrap',
    },
    text: {
        maxWidth: 760,
    },
    overline: {
        display: 'block',
        color: tokens.blue600,
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        marginBottom: theme.spacing(1),
    },
    overlineOnBrand: {
        color: tokens.goldSoft,
    },
    title: {
        marginBottom: theme.spacing(1),
        position: 'relative',
    },
    rule: {
        width: 56,
        height: 4,
        borderRadius: 2,
        backgroundColor: tokens.gold,
        marginTop: theme.spacing(2),
    },
    description: {
        color: tokens.muted,
        margin: 0,
        fontSize: '1rem',
        lineHeight: 1.7,
    },
    descriptionOnBrand: {
        color: 'rgba(255,255,255,0.82)',
    },
    onBrandTitle: {
        color: '#ffffff',
    },
    action: {
        flexShrink: 0,
    },
}));

/**
 * Section and state actions accept either a ready-made node or a descriptor
 * ({ label, to } for a route, { label, href } for an external link) so callers
 * do not have to repeat button markup.
 */
function renderAction(action, { onBrand = false, variant = 'outlined' } = {}) {
    if (!action) return null;
    if (React.isValidElement(action) || typeof action === 'string') return action;
    if (typeof action !== 'object' || !action.label) return null;

    const brandStyle = onBrand
        ? { color: '#ffffff', borderColor: 'rgba(255,255,255,0.55)' }
        : undefined;

    if (action.href) {
        return (
            <Button
                variant={variant}
                color={onBrand ? 'inherit' : 'primary'}
                style={brandStyle}
                href={action.href}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<ArrowForwardIcon />}
            >
                {action.label}
            </Button>
        );
    }

    return (
        <Button
            variant={variant}
            color={onBrand ? 'inherit' : 'primary'}
            style={brandStyle}
            component={RouterLink}
            to={action.to || '/'}
            endIcon={<ArrowForwardIcon />}
        >
            {action.label}
        </Button>
    );
}

/** Section heading with optional overline, rule, description and trailing action. */
export function SectionHeading({
    overline,
    title,
    description,
    action,
    onBrand = false,
    rule = true,
    titleVariant = 'h2',
}) {
    const classes = useHeadingStyles();

    return (
        <div className={classes.root}>
            <div className={classes.text}>
                {overline && (
                    <span className={`${classes.overline} ${onBrand ? classes.overlineOnBrand : ''}`}>
                        {overline}
                    </span>
                )}
                <Typography
                    variant={titleVariant}
                    component="h2"
                    className={`${classes.title} ${onBrand ? classes.onBrandTitle : ''}`}
                >
                    {title}
                </Typography>
                {description && (
                    <p className={`${classes.description} ${onBrand ? classes.descriptionOnBrand : ''}`}>
                        {description}
                    </p>
                )}
                {rule && !description && <div className={classes.rule} />}
            </div>
            {action && (
                <div className={classes.action}>{renderAction(action, { onBrand })}</div>
            )}
        </div>
    );
}

const useStateStyles = makeStyles((theme) => ({
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(10, 2),
        color: tokens.muted,
    },
    empty: {
        textAlign: 'center',
        padding: theme.spacing(8, 3),
        border: `1px dashed ${tokens.borderStrong}`,
        borderRadius: tokens.radius,
        backgroundColor: tokens.surface,
    },
    emptyTitle: {
        marginBottom: theme.spacing(1),
    },
    emptyText: {
        color: tokens.muted,
        margin: '0 auto',
        maxWidth: 460,
    },
    emptyAction: {
        marginTop: theme.spacing(3),
    },
}));

/** Accessible loading indicator, announced to assistive technology. */
export function LoadingState({ label = 'Loading content' }) {
    const classes = useStateStyles();

    return (
        <div className={classes.loading} role="status" aria-live="polite">
            <CircularProgress size={36} thickness={4} />
            <Typography variant="body2">{label}…</Typography>
        </div>
    );
}

/** Shown when a list or search returns nothing. */
export function EmptyState({
    title = 'Nothing to display',
    description = 'There is no content available here at the moment. Please check back later.',
    action,
}) {
    const classes = useStateStyles();

    return (
        <div className={classes.empty}>
            <Typography variant="h5" component="p" className={classes.emptyTitle}>
                {title}
            </Typography>
            <p className={classes.emptyText}>{description}</p>
            {action && (
                <div className={classes.emptyAction}>
                    {renderAction(action, { variant: 'contained' })}
                </div>
            )}
        </div>
    );
}

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        padding: theme.spacing(2.5, 3),
        marginBottom: theme.spacing(4),
        backgroundColor: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
    },
    label: {
        color: tokens.inkSoft,
        fontWeight: 600,
        fontSize: '0.875rem',
        marginRight: theme.spacing(0.5),
    },
    group: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    count: {
        color: tokens.muted,
        fontSize: '0.875rem',
    },
}));

/** Filter / search toolbar that sits above a result list. */
export function FilterBar({ children, resultCount, resultNoun = 'results' }) {
    const classes = useToolbarStyles();

    return (
        <div className={classes.root}>
            <div className={classes.group}>{children}</div>
            {typeof resultCount === 'number' && (
                <span className={classes.count} aria-live="polite">
                    {resultCount} {resultNoun}
                </span>
            )}
        </div>
    );
}

export function FilterLabel({ children }) {
    const classes = useToolbarStyles();
    return <span className={classes.label}>{children}</span>;
}

/** Renders trusted CMS HTML with the shared rich-text typography. */
export function RichText({ html, className = '' }) {
    if (!html) return null;

    return (
        <div
            className={`mof-rich-text ${className}`.trim()}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

const useLinkCardStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: theme.spacing(3),
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        borderTop: `3px solid ${tokens.blue600}`,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease',
        '&:hover': {
            boxShadow: tokens.shadowMd,
            transform: 'translateY(-2px)',
            borderTopColor: tokens.gold,
        },
    },
    icon: {
        color: tokens.blue600,
        marginBottom: theme.spacing(2),
        display: 'flex',
    },
    title: {
        marginBottom: theme.spacing(1),
    },
    description: {
        color: tokens.muted,
        fontSize: '0.9375rem',
        lineHeight: 1.65,
        margin: 0,
        flexGrow: 1,
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        marginTop: theme.spacing(2.5),
        color: tokens.blue600,
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
}));

/**
 * Navigational card. Uses a router link for internal paths and an anchor for
 * external URLs so that off-site links open in a new tab. `sameTab` keeps an
 * absolute URL in the current tab (e.g. a Ministry subdomain).
 */
export function LinkCard({ title, description, icon, to, href, sameTab = false, cta = 'Learn more' }) {
    const classes = useLinkCardStyles();
    const isExternal = Boolean(href);

    const content = (
        <>
            {icon && <span className={classes.icon}>{icon}</span>}
            <Typography variant="h6" component="h3" className={classes.title}>
                {title}
            </Typography>
            {description && <p className={classes.description}>{description}</p>}
            <span className={classes.cta}>
                {cta}
                <ArrowForwardIcon style={{ fontSize: 16 }} />
            </span>
        </>
    );

    if (isExternal) {
        return (
            <a
                className={classes.root}
                href={href}
                {...(sameTab ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            >
                {content}
            </a>
        );
    }

    return (
        <RouterLink className={classes.root} to={to || '#'}>
            {content}
        </RouterLink>
    );
}

export default PageHero;
